// @flow
import { CommonActions } from '@react-navigation/native';
import { all, call, put, select, takeEvery, delay } from 'redux-saga/effects';
import { tripica } from '../../api';
import config from '../../config';
import { trackAnalyticsEvent } from '../../lib/analytics';
import FormatService from '../../lib/format';
import HandleErrorService from '../../lib/handleError';
import I18n from '../../lib/i18n';
import Navigation from '../../lib/navigation';
import { deviceIdSelector } from '../Authentication/selectors';
import { customerOuidSelector, customerTaxExemptionsSelector } from '../Customer/selectors';
import {
  currentSubscriptionBillingAccountOuidSelector,
  currentSubscriptionIdSelector,
  currentSubscriptionNextRenewalDateSelector,
  currentSubscriptionRenewalStatusSelector,
  currentSubscriptionSelector,
} from '../Dashboard';
import { setLoading } from '../LoadingStatus/actions';
import { resetToDashboard } from '../Navigation/actions';
import { newOfferOptionsSelector, newOfferPriceSelector } from '../NewOffer/selectors';
import { fetchNextPlan, nextPlanUuidSelector } from '../NextPlan';
import { changePlanDateSelector } from '../Plan/selectors';
import { triggerPaymentProductOrderPolling, triggerProductOrderPolling } from '../PollingOrder/index';
import { fetchAll, phoneLinesSelector } from '../Subscriptions';
import { voucherCurrentVoucherDetailsSelector } from '../Voucher';
import type { SubscriptionAction } from './actions';
import { switchModeFailure, switchModeRequest, switchModeSuccess } from './actions';
import { lineSelector } from './selectors';
import WizzeeLog from '../../lib/wizzeeLog';

export function* switchMode(action: SubscriptionAction): Generator<*, *, *> {
  if (action.type !== 'SWITCH_PLAN_MODE') return; // for flow

  yield put(setLoading('switchPlanMode', true));
  yield put(switchModeRequest());

  const { mode } = action.payload;

  trackAnalyticsEvent('change_subscription_mode', {
    label: 'mode',
    value: mode ? 1 : 0,
  });

  const line = yield select(lineSelector);
  const customerOuid = yield select(customerOuidSelector);

  try {
    const response = yield call([tripica, 'changeSubscriptionMode'], customerOuid, line?.number, mode);
    if (!response) {
      throw new Error('No response');
    }
    if (response.status === 'VALIDATED') {
      const productOrder = yield call(triggerProductOrderPolling, response.uuid);
      if (productOrder) {
        yield put(switchModeSuccess(response));
      } else {
        yield put(switchModeFailure());
      }
      yield put(fetchAll());
    } else {
      throw new Error(response.errorCodeList.join(','));
    }
  } catch (error) {
    WizzeeLog.warn('[saga] switchMode', error);
    yield put(switchModeFailure());
    HandleErrorService.showToastError(I18n.t('error.orderFailed'));
  } finally {
    yield put(setLoading('switchPlanMode', false));
  }
}

export function* cancelPlan(action: SubscriptionAction): Generator<*, *, *> {
  if (action.type !== 'CANCEL_PLAN_REQUEST') return; // for flow
  const uuidPlan = yield select(nextPlanUuidSelector);
  try {
    yield call([tripica, 'deleteSandBoxByUuid'], uuidPlan);
    const subscription = yield select(currentSubscriptionSelector);
    yield put(fetchNextPlan(subscription));
  } catch (e) {
    WizzeeLog.warn('[saga] cancelPlan', e);
  }
}

export function* changePlan(): Generator<*, *, *> {
  const customerOuid = yield select(customerOuidSelector);
  const subscriptionOuid = yield select(currentSubscriptionIdSelector);
  const subscriptionRenewalStatus = yield select(currentSubscriptionRenewalStatusSelector);
  const billingAccountOuid = yield select(currentSubscriptionBillingAccountOuidSelector);
  const newOfferPriceRaw = yield select(newOfferPriceSelector);
  const options = yield select(newOfferOptionsSelector);
  const orderableOptions = options.filter(
    opt => opt.ouid && opt.characteristic?.value !== '0',
  );

  const productsToKeep = yield call([tripica, 'getSubscribedOccurrenceLimitedProducts'], subscriptionOuid);
  const expectedCompletionDate = config.canChangePlanNow
    ? yield select(changePlanDateSelector)
    : yield select(currentSubscriptionNextRenewalDateSelector);
  const taxExemptions = yield select(customerTaxExemptionsSelector);

  // Handling voucher if any selected
  const voucherDetails = yield select(voucherCurrentVoucherDetailsSelector);
  if (voucherDetails !== null) {
    // Gestion CTO à la hausse
    if (voucherDetails.characteristics && voucherDetails.characteristics.MINIMUM_ORDER_PRICE) {
      if (Number.isNaN(voucherDetails.characteristics.MINIMUM_ORDER_PRICE)) {
        HandleErrorService.showToastError(I18n.t('error.orderInvalidVoucher'));
        return;
      }
      const newOfferPrice = FormatService.formatCentsToCurrency(newOfferPriceRaw, false);
      const ctoUpgradeValue = voucherDetails.characteristics.MINIMUM_ORDER_PRICE / 100;
      if (Number(newOfferPrice) <= ctoUpgradeValue) {
        HandleErrorService.showToastError(I18n.t('error.orderInvalidCtoUpVoucher'));
        return;
      }
    }
  }

  try {
    yield put(setLoading('changePlanConfirmation', true));
    const response = yield call(
      [tripica, 'changePlan'],
      customerOuid,
      subscriptionOuid,
      billingAccountOuid,
      orderableOptions,
      expectedCompletionDate,
      [],
      taxExemptions,
      productsToKeep,
      voucherDetails
    );
    if (!response) {
      throw new Error('No response');
    }
    if (response.status === 'VALIDATED') {
      if (!expectedCompletionDate) {
        const isPaymentOrder = subscriptionRenewalStatus === 'OFF';
        const productOrder = yield call(
          isPaymentOrder ? triggerPaymentProductOrderPolling : triggerProductOrderPolling,
          'CHANGE_PLAN',
          response.uuid
        );
        if (productOrder.state === 'COMPLETED') {
          yield call(Navigation.dispatch, CommonActions.navigate({ name: 'PaymentSuccess' }));
        } else if (productOrder.state === 'IN_PROGRESS') {
          yield call(Navigation.dispatch, CommonActions.navigate({ name: 'PaymentInProgress' }));
        } else {
          yield call(Navigation.dispatch, CommonActions.navigate({ name: 'PaymentFailure' }));
        }
      } else {
        yield delay(1000);
        yield call(resetToDashboard);
      }
    } else {
      throw new Error(response.errorCodeList.join(','));
    }
  } catch (e) {
    WizzeeLog.warn('[saga] changePlanConfirmation', e);
    HandleErrorService.showToastError(I18n.t('error.orderFailed'));
    yield call(resetToDashboard);
  } finally {
    yield put(setLoading('changePlanConfirmation', false));
  }
}

export function* saveAsFirstSubscription(action: SubscriptionAction): Generator<*, *, *> {
  if (action.type !== 'SAVE_AS_FIRST_SUBSCRIPTION') return;
  const { subscription } = action.payload;

  try {
    if (subscription.status === 'ACTIVE') {
      const currentPhoneLines = yield select(phoneLinesSelector);
      const deviceId = yield select(deviceIdSelector);
      const orderedPhoneLines = currentPhoneLines.sort((lineA, lineB) => {
        if (lineB.number === subscription.line.number) return 999;
        if (lineA.number === subscription.line.number) return -999;
        return lineA.rank - lineB.rank;
      });

      yield call([tripica, 'changePhoneLinesPreferences'], orderedPhoneLines, deviceId);
    }
  } catch (e) {
    WizzeeLog.warn('rankSubscriptionAsFirst', e);
    // fail silently
  }
}

export default function*(): Generator<*, *, *> {
  yield all([
    takeEvery('SWITCH_PLAN_MODE', switchMode),
    takeEvery('CANCEL_PLAN_REQUEST', cancelPlan),
    takeEvery('CHANGE_PLAN', changePlan),
    takeEvery('SAVE_AS_FIRST_SUBSCRIPTION', saveAsFirstSubscription),
  ]);
}
